import Box from "@mui/material/Box";
import { useEffect, useState } from "react";

import { MID, TINY, marketingPalette } from "@/components/marketing/tokens";
import type { CallToAction } from "@/content/types";
import PostCtaCard from "./PostCtaCard";

/** The published page's own delay, from its script: five seconds after load. */
const POPUP_DELAY_MS = 5000;

const CROSS = "/marketing/webflow/665ac3e28722e6cb280c0bde_cross.svg";

/** `.blog-pop-up_overlay`: the dimmed page, drawn only below the card step. */
const OVERLAY = {
  display: "none",
  [MID]: { display: "flex" },
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  alignItems: "center",
  justifyContent: "center",
  px: "35.2px",
  backgroundColor: "rgba(0, 0, 0, 0.69)",
  [TINY]: { px: "16px" },
} as const;

/** `.blog-pop-up_close_trigger`: the dimmed page itself, which closes it. */
const CLOSE_TRIGGER = { position: "absolute", inset: 0 } as const;

/** `.blog-cta-pop-up_wrap` and its `.blog-cta_pop-up-box`. */
const PANEL = {
  position: "relative",
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  width: "480px",
  p: "16px",
  borderRadius: "16px",
  overflow: "hidden",
  backgroundColor: "common.white",
  [MID]: { width: 1 },
} as const;

/** `.pop-up_top-title-icon_wrap`: the round cross in the panel's corner. */
const CLOSE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  p: 0,
  border: 0,
  borderRadius: "100vw",
  backgroundColor: "transparent",
  cursor: "pointer",
  transition: "all 0.22s cubic-bezier(0.614, 0.521, 0.041, 1.007)",
  "&:hover": { backgroundColor: marketingPalette.wash },
} as const;

/**
 * The published post's pop-up: on a phone, five seconds after the page
 * opens, the post's call to action over the dimmed page, until its cross, a
 * tap beside it, or Escape closes it for the rest of the visit. Wider pages
 * show the same card in the article's rail and draw no pop-up.
 */
export default function PostCtaPopup({ cta }: { cta: CallToAction }) {
  const [open, setOpen] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setOpen(true), POPUP_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open || closed) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setClosed(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closed]);

  if (!open || closed) return null;
  return (
    <Box role="dialog" aria-modal="true" aria-label={cta.title} sx={OVERLAY}>
      <Box aria-hidden onClick={() => setClosed(true)} sx={CLOSE_TRIGGER} />
      <Box sx={PANEL}>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={() => setClosed(true)}
          sx={CLOSE}
        >
          <Box
            component="img"
            src={CROSS}
            alt=""
            sx={{ width: "19.2px", height: "19.2px" }}
          />
        </Box>
        <Box sx={{ width: 1, mt: "8px" }}>
          <PostCtaCard cta={cta} variant="below" />
        </Box>
      </Box>
    </Box>
  );
}
